import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import Seo from "../components/Seo";
import SectionEyebrow from "../components/SectionEyebrow";
import RevealOnScroll from "../components/RevealOnScroll";
import Button from "../components/Button";
import Lightbox from "../components/Lightbox";
import CaseStudyHero from "../components/CaseStudyHero";
import { WORKS } from "../data/content";
import { breadcrumbSchema } from "../lib/schemas";

const SchedaRow = ({ label, children }) => (
  <div className="grid grid-cols-12 gap-3 py-3 border-b border-current/15 text-[14px] leading-[1.55]">
    <dt className="col-span-5 md:col-span-4 font-condensed uppercase tracking-widest text-[12px] md:text-[13px] opacity-70">
      {label}
    </dt>
    <dd className="col-span-7 md:col-span-8">{children}</dd>
  </div>
);

const ApproccioBlock = ({ num, title, body }) => (
  <div className="border border-galactico/15 p-7 md:p-9 bg-galactico/[0.03]">
    <SectionEyebrow num={num}>{title}</SectionEyebrow>
    <p className="mt-5 text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[48ch]">
      {body}
    </p>
  </div>
);

const KpiBlock = ({ value, label }) => (
  <div className="border-t border-galactico/25 pt-6">
    <div
      className="font-display leading-[0.92] tracking-tight text-galactico"
      style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}
    >
      {value}
    </div>
    <div className="font-condensed uppercase tracking-widest text-[12px] md:text-[13px] mt-4 opacity-85 max-w-[24ch] leading-[1.35]">
      {label}
    </div>
  </div>
);

const YouTubeEmbed = ({ id, title, label, caption }) => (
  <div>
    <div className="font-condensed uppercase tracking-widest text-[14px] text-lime mb-3">
      {label}
    </div>
    <div className="aspect-video w-full overflow-hidden bg-dodgers/10">
      <iframe
        src={`https://www.youtube.com/embed/${id}`}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        loading="lazy"
        className="w-full h-full border-0"
        data-testid={`yt-${id}`}
      />
    </div>
    <p
      className="mt-3 text-[14px] leading-[1.55] text-mandrogni"
      style={{ fontStyle: "italic" }}
    >
      {caption}
    </p>
  </div>
);

// Galleria sezione 6 — 7 slot, mapping 1:1 stretto (slot i ↔ GALLERY_PHOTOS[i]).
// Layout magazine 12 col: 1 full (12), 2-4 (4 each), 5 full (12), 6-7 (6 each).
// Slot oltre la lunghezza dell'array mostrano placeholder "Foto in arrivo".
const GALLERY_SLOTS = 7;
const GALLERY_PHOTOS = [
  // 01 — Ragazze in maglia rossa, una ride coprendo la bocca, pallone Mikasa giallo/blu
  "/images/assets/3pb7sube_20221017_15-28-55_z62_9552-980x653.jpg",
  // 02 — Testimonial inquadrato dal finestrino del van, sguardo concentrato
  "/images/assets/7xth1bp4_20221017_11-34-42_z62_9050-980x653.jpg",
  // 03 — Due ragazze Athena Volley in palestra con pallone Mikasa firmato
  "/images/assets/m2c2uqow_20220607_20220607_12-05-40_z62_8734.jpg",
  // 04 — Ragazzi della società Athena seduti all'esterno, maglie verdi/nere
  "/images/assets/0fwg9e9s_20220607_20220607_15-25-21_z62_9429-980x652.jpg",
  // 05 — Cristina Chirichella e Alberto Cisolla in palestra parlano alle ragazze, dietro la rete
  "/images/assets/6gwdg4iy_01---credem.jpg",
  // 06 — Ragazze ascoltano in semicerchio durante l'incontro con i testimonial, pallone Mikasa
  "/images/assets/nvh9mkd2_20220607_20220607_15-02-19_z62_9198-980x652.jpg",
  // 07 — Il van "Extra-ordinary" Credem Banca arrivato presso una società sportiva
  "/images/assets/xpykt58m_20220607_20220607_15-26-20_z62_9442-980x652.jpg",
];
const LIGHTBOX_IMAGES = GALLERY_PHOTOS.map((src, i) => ({
  src,
  alt: `Extra Ordinary — Credem Banca — Asset ${String(i + 1).padStart(2, "0")} di ${GALLERY_SLOTS}`,
}));

// Layout magazine helper — riga 1: 1 slot full (12) · riga 2: 3 slot (4 ciascuno) ·
// riga 3: 1 slot full (12) · riga 4: 2 slot (6 ciascuno).
const SLOT_LAYOUT = [
  "col-span-4 md:col-span-12",  // 01 full
  "col-span-4 md:col-span-4",   // 02
  "col-span-4 md:col-span-4",   // 03
  "col-span-4 md:col-span-4",   // 04
  "col-span-4 md:col-span-12",  // 05 full
  "col-span-4 md:col-span-6",   // 06
  "col-span-4 md:col-span-6",   // 07
];

const CreditRow = ({ role, name }) => (
  <div className="grid grid-cols-12 gap-3 py-2 text-[13px] leading-[1.55] text-mandrogni">
    <dt className="col-span-6 font-condensed uppercase tracking-widest text-[11px] md:text-[12px] opacity-80">
      {role}
    </dt>
    <dd className="col-span-6 opacity-90">{name}</dd>
  </div>
);

const WorkCredemExtraOrdinary = () => {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    if (lightboxIndex !== null) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [lightboxIndex]);

  const idx = WORKS.findIndex((w) => w.to === "/works/credem-extra-ordinary");
  const prev = idx > 0 ? WORKS[idx - 1] : WORKS[WORKS.length - 1];
  const next = idx >= 0 ? WORKS[(idx + 1) % WORKS.length] : WORKS[0];

  const breadcrumbJsonLd = breadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "Works", url: "/works" },
    { name: "Credem Extra Ordinary", url: "/works/credem-extra-ordinary" },
  ]);

  return (
    <>
      <Seo
        title="Credem Extra Ordinary: 4 testimonial azzurri | OnSports Case"
        description={"Format editoriale Credem Banca \"Extra Ordinary\" con 4 testimonial della pallavolo italiana. Title sponsor Lega Pallavolo. OnSports Content & Sponsorship, 2022."}
        path="/works/credem-extra-ordinary"
        image="/images/assets/3pb7sube_20221017_15-28-55_z62_9552-980x653.jpg"
        jsonLd={breadcrumbJsonLd}
      />

      {/* SEZ. 1 — HERO */}
      <CaseStudyHero
        imagePath="/images/works/credem-extra-ordinary/hero.jpg"
        imageAlt="Extra Ordinary — Credem Banca × Problemi di Volley"
        eyebrow="● Case study · Social Impact · Content & IP Studio"
        titleLines={["Un van. Tre tappe.", "Un piccolo EXTRA."]}
        highlightWord="EXTRA."
        subtitle="Abbiamo trasformato la sponsorship istituzionale di Credem Banca per la SuperLega di volley maschile in un progetto di responsabilità sociale dal forte impatto narrativo: tre tappe italiane, quattro azzurri testimonial, tre video editoriali e donazioni concrete alle società sportive di base che fanno del volley uno strumento di opportunità per i loro ragazzi."
        meta="2022  ·  Cliente — Credem Banca  ·  Categoria — Social Impact & Brand Journalism"
        overlayOpacity={0.6}
      />

      {/* SEZ. 2 — OVERVIEW */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-overview">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-7">
              <RevealOnScroll>
                <SectionEyebrow>Overview</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Il progetto.</h2>
              </RevealOnScroll>
              <div className="mt-8 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[760px]">
                <RevealOnScroll delay={0.15}>
                  <p>
                    Credem Banca è title sponsor della SuperLega di volley
                    maschile italiana, il massimo campionato nazionale che oggi
                    porta ufficialmente il nome "Lega Pallavolo SuperLega Credem
                    Banca". È una sponsorship istituzionale di vertice, capace
                    di garantire visibilità altissima a ogni weekend di
                    campionato. Ma con il limite strutturale di ogni partnership
                    di pura presenza commerciale: si vede, non si racconta.
                  </p>
                </RevealOnScroll>
                <RevealOnScroll delay={0.2}>
                  <p>
                    Quando Credem Banca ci ha coinvolti, l'obiettivo era
                    costruire un livello editoriale più profondo dentro alla
                    partnership — un progetto che parlasse non del volley in
                    vetrina, ma del volley di base. Quello giocato nelle
                    palestre dei quartieri, nelle scuole di periferia, nei
                    centri sportivi gestiti da associazioni che hanno fatto del
                    volley uno strumento di opportunità sociale per i giovani
                    che altrimenti non avrebbero accesso allo sport.
                  </p>
                </RevealOnScroll>
                <RevealOnScroll delay={0.25}>
                  <p>
                    Extra Ordinary nasce da questa intuizione. Un format
                    editoriale scritto da OnSports e Problemi di Volley, che ha
                    portato un van attraverso l'Italia per documentare le storie
                    di società sportive impegnate nel sociale, raccontandole
                    insieme a quattro grandi nomi del volley italiano: Alberto
                    Cisolla, Cristina Chirichella, Matteo Piano, Simone
                    Giannelli. Credem Banca ha donato a ciascuna delle società
                    visitate attrezzature, abbigliamento e materiale tecnico,
                    trasformando il racconto in un atto concreto.
                  </p>
                </RevealOnScroll>
              </div>
            </div>
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll delay={0.25}>
                <div className="border border-dodgers/20 p-6 md:p-7" data-testid="scheda-progetto">
                  <span className="font-condensed uppercase tracking-widest text-xs opacity-60 block mb-5">
                    Scheda progetto
                  </span>
                  <dl>
                    <SchedaRow label="Progetto">Extra Ordinary</SchedaRow>
                    <SchedaRow label="Cliente">Credem Banca</SchedaRow>
                    <SchedaRow label="Testimonial">
                      Alberto Cisolla · Cristina Chirichella<br />
                      Matteo Piano · Simone Giannelli
                    </SchedaRow>
                    <SchedaRow label="Ruolo">
                      Concept &amp; writing · Organizzazione<br />
                      Produzione foto e video<br />
                      Seeding &amp; media mix
                    </SchedaRow>
                    <SchedaRow label="Pilastro">
                      Social Impact · Content &amp; IP Studio
                    </SchedaRow>
                    <SchedaRow label="Format">
                      IP proprietaria Problemi di Volley
                    </SchedaRow>
                    <SchedaRow label="Anno">2022</SchedaRow>
                  </dl>
                </div>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 3 — LA SFIDA */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-sfida">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll>
                <SectionEyebrow>La sfida</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Dare profondità a una sponsorship di vertice.
                </h2>
              </RevealOnScroll>
            </div>
            <div className="col-span-12 md:col-span-7 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[64ch] opacity-90">
              <RevealOnScroll delay={0.15}>
                <p>
                  Le sponsorship istituzionali — title sponsor di una lega
                  nazionale, naming sponsor di un campionato — hanno una potenza
                  commerciale enorme ma una superficie di racconto sottile. Il
                  nome del brand entra in ogni telecronaca, in ogni grafica
                  delle partite, in ogni titolo dei giornali. Ma resta sempre
                  nello stesso registro: presenza, non narrazione.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.22}>
                <p>
                  Per un brand come Credem Banca, che ha nei propri valori
                  dichiarati la prossimità al territorio e l'attenzione alle
                  comunità locali, il rischio era quello di una sponsorship di
                  pura visibilità che non comunicasse alcun valore differenziale
                  rispetto ai concorrenti del settore bancario. La sfida era
                  costruire un'estensione editoriale della sponsorship capace di
                  incarnare i valori dichiarati del brand in storie concrete, e
                  di farlo dentro un format proprietario credibile per la
                  community del volley.
                </p>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 4 — IL FORMAT / 3 VIDEO YOUTUBE */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-format">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10 md:mb-14">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll>
                <SectionEyebrow>Extra Ordinary</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Tre tappe. Quattro azzurri. Storie che meritavano un
                  palcoscenico.
                </h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[68ch] opacity-90">
                  Tre puntate, tre tappe italiane, ognuna costruita attorno a
                  una società sportiva del territorio e a uno o due atleti
                  azzurri come ospiti d'eccezione. Abbiamo seguito gli incontri
                  tra i campioni e i ragazzi delle palestre, raccolto le storie
                  delle associazioni che li gestiscono, raccontato il
                  significato di "extra" che ognuna di queste realtà mette nel
                  proprio lavoro quotidiano.
                </p>
              </RevealOnScroll>
            </div>
          </div>

          <div className="mx-auto max-w-[1100px] space-y-12 md:space-y-16" data-testid="yt-grid">
            <RevealOnScroll delay={0.05}>
              <YouTubeEmbed
                id="11_n6N5BeIg"
                title="Extra Ordinary — Tappa 1 con Cristina Chirichella"
                label="Tappa 1 · con Cristina Chirichella"
                caption="La prima tappa del viaggio Extra Ordinary."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.12}>
              <YouTubeEmbed
                id="xV9Os3pi5mY"
                title="Extra Ordinary — Tappa 2 con Matteo Piano"
                label="Tappa 2 · con Matteo Piano"
                caption="Il secondo capitolo del viaggio Extra Ordinary."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.19}>
              <YouTubeEmbed
                id="I-VBaCpikAQ"
                title="Extra Ordinary — Tappa 3 con Simone Giannelli"
                label="Tappa 3 · con Simone Giannelli"
                caption="Terza e ultima tappa del format."
              />
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* SEZ. 5 — APPROCCIO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-approccio">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll>
                <SectionEyebrow>Approccio</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Dal concept editoriale alla consegna concreta.
                </h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[68ch] opacity-90">
                  Abbiamo gestito il progetto end-to-end: dall'idea iniziale
                  alla scrittura del format, dalla regia operativa del van che
                  ha attraversato l'Italia alla produzione integrale dei
                  contenuti, fino al media mix di amplificazione. Ogni tappa è
                  stata costruita come una piccola produzione editoriale
                  autonoma, ma agganciata a un'identità di serie riconoscibile.
                </p>
              </RevealOnScroll>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-7">
            <RevealOnScroll delay={0.05}>
              <ApproccioBlock
                num="01"
                title="Concept & writing"
                body="Definizione del territorio narrativo Extra Ordinary, sviluppo del claim 'la differenza tra ordinario e straordinario è solo un piccolo extra', scrittura del format e identificazione delle tre tappe in collaborazione con Credem Banca e Problemi di Volley. Selezione delle società sportive da raccontare in base alla loro effettiva impronta sociale, non a logiche di geografia o visibilità."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.12}>
              <ApproccioBlock
                num="02"
                title="Organizzazione & produzione"
                body="Coordinamento operativo del van che ha attraversato l'Italia: logistica delle tappe, gestione dei testimonial azzurri (disponibilità, contratti immagine, allineamento con federazione e club), accordi con le società ospitanti, calendarizzazione delle riprese. Produzione integrale di video, foto e materiali editoriali."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.19}>
              <ApproccioBlock
                num="03"
                title="Donazioni e consegna fisica"
                body="Credem Banca ha donato a ciascuna delle società visitate attrezzature tecniche, abbigliamento e palloni da volley. Le donazioni sono diventate parte del racconto stesso — non un gesto a margine ma il punto narrativo di chiusura di ogni tappa: la storia raccontata si concludeva con uno strumento concreto consegnato alla società."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.26}>
              <ApproccioBlock
                num="04"
                title="Media mix & seeding"
                body="Distribuzione coordinata su due livelli: i canali owned di Credem Banca (sito, social corporate, materiali per la comunicazione interna) e la community Problemi di Volley come amplificatore verticale presso il pubblico volley italiano. Format pensato fin dall'inizio per essere distribuito in episodi seriali, costruendo una curva di attenzione nelle settimane successive a ogni tappa."
              />
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* SEZ. 6 — GALLERIA */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-gallery">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10 md:mb-14">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll>
                <SectionEyebrow>Visual</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Il viaggio, in immagini.</h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[60ch] opacity-90">
                  Una selezione del photo report del progetto: gli incontri tra
                  i campioni e i ragazzi delle società, i momenti del viaggio,
                  le consegne finali del materiale tecnico.
                </p>
              </RevealOnScroll>
            </div>
          </div>

          <div
            className="grid grid-cols-4 md:grid-cols-12 gap-3"
            data-testid="gallery-grid"
          >
            {Array.from({ length: GALLERY_SLOTS }).map((_, i) => {
              const wrapperClass = SLOT_LAYOUT[i];
              const photo = GALLERY_PHOTOS[i];

              if (!photo) {
                return (
                  <RevealOnScroll
                    key={i}
                    delay={Math.min(i, 6) * 0.04}
                    className={wrapperClass}
                  >
                    <div
                      className="block w-full relative bg-dodgers/[0.05] border border-lime border-dashed flex items-center justify-center"
                      style={{ aspectRatio: "4 / 3" }}
                      data-testid={`gallery-placeholder-${i}`}
                      aria-label={`Asset ${i + 1} di ${GALLERY_SLOTS} — in arrivo`}
                    >
                      <span
                        className="font-condensed uppercase tracking-widest text-galactico"
                        style={{ fontSize: "12px" }}
                      >
                        Foto in arrivo
                      </span>
                    </div>
                  </RevealOnScroll>
                );
              }

              return (
                <RevealOnScroll
                  key={i}
                  delay={Math.min(i, 6) * 0.04}
                  className={wrapperClass}
                >
                  <button
                    type="button"
                    onClick={() => setLightboxIndex(i)}
                    className="block w-full overflow-hidden relative group focus:outline-none bg-dodgers/[0.04]"
                    style={{ aspectRatio: "4 / 3" }}
                    data-testid={`gallery-${i}`}
                    data-hover="true"
                    aria-label={`Apri asset ${i + 1} di ${GALLERY_SLOTS}`}
                  >
                    <img
                      src={photo}
                      alt={LIGHTBOX_IMAGES[i].alt}
                      loading="lazy"
                      className="block w-full h-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.02]"
                      draggable="false"
                    />
                    <span
                      className="absolute inset-0 pointer-events-none border-[1.5px] border-lime opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-hidden="true"
                    />
                  </button>
                </RevealOnScroll>
              );
            })}
          </div>
        </div>
      </section>

      <Lightbox
        images={LIGHTBOX_IMAGES}
        openIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
      />

      {/* SEZ. 7 — IMPATTO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-impatto">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12 md:mb-16">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll>
                <SectionEyebrow>Impatto</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Quando una sponsorship istituzionale diventa storia.
                </h2>
              </RevealOnScroll>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
            <RevealOnScroll delay={0.05}>
              <KpiBlock value="3" label="Tappe documentate lungo l'Italia" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.1}>
              <KpiBlock value="4" label="Azzurri testimonial ospiti del format" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.15}>
              <KpiBlock value="3" label="Società sportive di base beneficiarie" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.2}>
              <KpiBlock value="Social" label="Responsibility project end-to-end" />
            </RevealOnScroll>
          </div>

          <p className="mt-8 text-[13px] leading-[1.55] text-mandrogni opacity-80 max-w-[80ch]">
            Format editoriale realizzato per Credem Banca, title sponsor della
            Lega Pallavolo SuperLega Credem Banca. Progetto scritto da OnSports
            e dalla community Problemi di Volley, anno 2022.
          </p>

          <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            <RevealOnScroll delay={0.05}>
              <div>
                <h4 className="font-condensed uppercase tracking-widest text-sm md:text-[15px] mb-3 text-lime">
                  Profondità sopra alla visibilità
                </h4>
                <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[40ch]">
                  La title sponsorship di Credem sulla SuperLega garantisce
                  esposizione ad alto volume ogni weekend del campionato, ma
                  esposizione è una dimensione superficiale. Extra Ordinary ha
                  aggiunto una dimensione profonda — il racconto di storie
                  reali, di società di base, di donazioni concrete — che
                  permette al brand di esistere nell'immaginario del pubblico
                  volley con un significato distinguibile e non solo con un
                  nome riconoscibile.
                </p>
              </div>
            </RevealOnScroll>
            <RevealOnScroll delay={0.12}>
              <div>
                <h4 className="font-condensed uppercase tracking-widest text-sm md:text-[15px] mb-3 text-lime">
                  Quattro testimonial sopra le proprie statistiche
                </h4>
                <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[40ch]">
                  Cisolla, Chirichella, Piano e Giannelli non sono apparsi nel
                  format come endorsement. Sono entrati nelle palestre, hanno
                  parlato con i ragazzi, hanno raccontato le proprie storie agli
                  allenatori delle società di base. Un uso del talent
                  radicalmente diverso dalla classica "presenza in spot", che
                  richiede tempo, organizzazione e disponibilità reale ma che
                  produce contenuto di un'autorialità non raggiungibile
                  altrimenti.
                </p>
              </div>
            </RevealOnScroll>
            <RevealOnScroll delay={0.19}>
              <div>
                <h4 className="font-condensed uppercase tracking-widest text-sm md:text-[15px] mb-3 text-lime">
                  Un format proprietario, replicabile
                </h4>
                <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[40ch]">
                  Extra Ordinary non si è esaurito nelle tre tappe del 2022. È
                  diventato un'IP editoriale di Problemi di Volley + Credem
                  Banca, replicabile per cicli successivi, scalabile in altri
                  territori e in altre discipline sportive. È il modello che
                  proponiamo a ogni brand che vuole far evolvere la propria
                  sponsorship istituzionale in un programma di impatto
                  strutturato.
                </p>
              </div>
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* SEZ. 8 — CREDITS */}
      <section className="py-20 md:py-28 bg-galactico text-dodgers" data-testid="section-credits">
        <div className="container-os">
          <RevealOnScroll>
            <SectionEyebrow>Credits</SectionEyebrow>
          </RevealOnScroll>
          <div className="mt-8 grid grid-cols-12 gap-8 md:gap-12">
            <RevealOnScroll delay={0.1} className="col-span-12 md:col-span-7">
              <div className="max-w-[640px]">
                <dl>
                  <SchedaRow label="Cliente">Credem Banca</SchedaRow>
                  <SchedaRow label="Progetto">Extra Ordinary</SchedaRow>
                  <SchedaRow label="Testimonial">
                    Alberto Cisolla · Cristina Chirichella<br />
                    Matteo Piano · Simone Giannelli
                  </SchedaRow>
                  <SchedaRow label="Ruolo OnSports">
                    Concept &amp; writing · Organizzazione<br />
                    Produzione video e foto · Media mix
                  </SchedaRow>
                  <SchedaRow label="Servizi">
                    Content &amp; IP Studio · Sponsorship &amp; Impact Programs
                  </SchedaRow>
                  <SchedaRow label="Format">
                    IP proprietaria Problemi di Volley
                  </SchedaRow>
                  <SchedaRow label="Anno">2022</SchedaRow>
                </dl>
              </div>
            </RevealOnScroll>
            <RevealOnScroll delay={0.18} className="col-span-12 md:col-span-5">
              <div className="border-l border-dodgers/15 md:pl-8">
                <span className="font-condensed uppercase tracking-widest text-xs opacity-60 block mb-4">
                  Team del progetto
                </span>
                <dl>
                  <CreditRow role="Creative Director" name="Matteo Sciascia" />
                  <CreditRow role="Written by" name="Tania Loschi" />
                  <CreditRow role="Video Making" name="Nello Giordano" />
                  <CreditRow role="Sound" name="Gabriele Cardullo" />
                  <CreditRow role="Photography" name="Lorenzo Morandi" />
                  <CreditRow role="Social Media" name="Tommaso Dotta" />
                  <CreditRow role="Client Manager" name="Nicole Galimberti" />
                </dl>
              </div>
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* SEZ. 9 — CTA FINALE */}
      <section
        className="bg-dodgers text-galactico py-24 md:py-40 border-t border-galactico/10"
        data-testid="section-cta-finale"
      >
        <div className="container-os">
          <RevealOnScroll>
            <h2 className="display-h2 max-w-[30ch]">
              Hai una sponsorship istituzionale che vorresti far evolvere in
              qualcosa di più? Costruiamo insieme il suo{" "}
              <span className="text-lime">IMPATTO</span>.
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <p className="mt-8 body-lg opacity-85 max-w-[62ch]">
              Lavoriamo con banche, assicurazioni e corporate che hanno
              partnership di vertice nel mondo sport e vogliono attivarle con
              programmi di Social Impact strutturati. Format proprietari, storie
              reali, donazioni concrete.
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.18}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contatti" data-testid="cta-parliamone">
                <Button variant="primary">Parliamone</Button>
              </Link>
              <Link to="/works" data-testid="cta-altri-casi">
                <Button variant="secondary-light">Vedi altri casi studio</Button>
              </Link>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 10 — NAVIGAZIONE PROGETTI */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-nav-progetti">
        <div className="container-os">
          <RevealOnScroll>
            <SectionEyebrow>Naviga i progetti</SectionEyebrow>
          </RevealOnScroll>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link
              to={prev.to || "/works"}
              className="group border border-dodgers/20 p-7 md:p-10 hover:border-dodgers transition-colors flex flex-col gap-4"
              data-hover="true"
              data-testid="nav-prev"
            >
              <span className="font-condensed uppercase tracking-widest text-xs opacity-70">
                ← Progetto precedente
              </span>
              <span className="font-display text-2xl md:text-3xl leading-tight group-hover:text-mandrogni transition-colors">
                {prev.title}
              </span>
              <span className="text-sm opacity-75">{prev.category}</span>
            </Link>
            <Link
              to={next.to || "/works"}
              className="group border border-dodgers/20 p-7 md:p-10 hover:border-dodgers transition-colors flex flex-col gap-4 md:text-right md:items-end"
              data-hover="true"
              data-testid="nav-next"
            >
              <span className="font-condensed uppercase tracking-widest text-xs opacity-70 inline-flex items-center gap-2">
                Progetto successivo <ArrowUpRight size={14} />
              </span>
              <span className="font-display text-2xl md:text-3xl leading-tight group-hover:text-mandrogni transition-colors">
                {next.title}
              </span>
              <span className="text-sm opacity-75">{next.category}</span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default WorkCredemExtraOrdinary;
